import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/authStore';
import { validateUsername, validatePassword } from '@/lib/utils';
import { ZapOverlay } from './ZapOverlay';

interface TUIRegistrationProps {
  onClose: () => void;
}

type Step = 'username' | 'password' | 'confirm' | 'recovery' | 'submitting' | 'done';

const PASSWORD_RULES = [
  { label: '8+ chars',   test: (v: string) => v.length >= 8 },
  { label: 'uppercase',  test: (v: string) => /[A-Z]/.test(v) },
  { label: 'lowercase',  test: (v: string) => /[a-z]/.test(v) },
  { label: 'number',     test: (v: string) => /\d/.test(v) },
];

/**
 * Full-screen TUI-style step-by-step registration terminal.
 * Guides the user through: codename → password → confirm → recovery email.
 * On success: plays "initializing" sequence then zaps closed.
 */
export function TUIRegistration({ onClose }: TUIRegistrationProps) {
  const [step, setStep] = useState<Step>('username');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [recovery, setRecovery] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [usernameStatus, setUsernameStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const [initLines, setInitLines] = useState<string[]>([]);
  const [zapping, setZapping] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { register, error: authError, clearError } = useAuthStore();

  // Focus input on step change
  useEffect(() => {
    inputRef.current?.focus();
  }, [step]);

  // Debounced username availability check
  useEffect(() => {
    if (step !== 'username') return;
    if (!username || validateUsername(username)) {
      setUsernameStatus('idle');
      return;
    }
    setUsernameStatus('checking');
    const timer = setTimeout(async () => {
      const { data } = await supabase
        .from('profiles')
        .select('id')
        .eq('username', username)
        .single();
      setUsernameStatus(data ? 'taken' : 'available');
    }, 400);
    return () => clearTimeout(timer);
  }, [username, step]);

  const passwordScore = PASSWORD_RULES.filter(r => r.test(password)).length;

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    advance();
  };

  const advance = () => {
    setFieldError('');
    clearError();

    if (step === 'username') {
      const err = validateUsername(username);
      if (err) { setFieldError(err); return; }
      if (usernameStatus === 'taken') { setFieldError('Codename already taken.'); return; }
      if (usernameStatus === 'checking') { setFieldError('Verifying codename...'); return; }
      setStep('password');
    } else if (step === 'password') {
      const err = validatePassword(password);
      if (err) { setFieldError(err); return; }
      setStep('confirm');
    } else if (step === 'confirm') {
      if (password !== confirm) { setFieldError('Keys do not match.'); return; }
      setStep('recovery');
    } else if (step === 'recovery') {
      submitRegistration();
    }
  };

  const submitRegistration = async () => {
    setStep('submitting');
    // Animated init sequence
    const lines = [
      '> ENCRYPTING IDENTITY...',
      '> ALLOCATING CLEARANCE...',
      '> ESTABLISHING SECURE CHANNEL...',
      `> PROFILE INITIALIZED. WELCOME, ${username.toUpperCase()}.`,
    ];

    try {
      await register(username, password, recovery || undefined);
      // Typewriter the init lines
      for (let i = 0; i < lines.length; i++) {
        await new Promise<void>((res) => setTimeout(res, i === lines.length - 1 ? 600 : 380));
        setInitLines(prev => [...prev, lines[i]]);
      }
      setStep('done');
      // Brief pause, then zap
      await new Promise<void>((res) => setTimeout(res, 700));
      setZapping(true);
    } catch {
      // authError is set in store; fall back to username step with error shown
      setStep('username');
    }
  };

  const handleZapComplete = () => {
    // Auth state change triggered by register() will update the session.
    // onClose collapses the TUI; Desktop re-renders for the normie phase.
    onClose();
  };

  const currentValue =
    step === 'username' ? username :
    step === 'password' ? password :
    step === 'confirm'  ? confirm  :
    step === 'recovery' ? recovery : '';

  const setCurrent = (v: string) => {
    if (step === 'username') setUsername(v);
    else if (step === 'password') setPassword(v);
    else if (step === 'confirm') setConfirm(v);
    else if (step === 'recovery') setRecovery(v);
  };

  const isPasswordStep = step === 'password' || step === 'confirm';

  return (
    <div className="fixed inset-0 z-[200000] flex flex-col items-center justify-center bg-matrix-black">
      {/* CRT overlays */}
      <div className="crt-overlay" />
      <div className="noise-overlay" />

      <div className="relative z-10 w-full max-w-lg px-6">
        {/* Header */}
        <pre className="text-matrix-green font-code text-[10px] leading-tight mb-6 select-none">{`
╔══════════════════════════════════════╗
║  NEW AGENT INITIALIZATION PROTOCOL  ║
║  CLEARANCE LEVEL: APPLICANT          ║
╚══════════════════════════════════════╝`}</pre>

        {/* Completed steps */}
        <div className="font-code text-sm space-y-1 mb-4">
          {step !== 'username' && step !== 'submitting' && step !== 'done' && (
            <p className="text-matrix-green/50">{'>'} CODENAME: <span className="text-matrix-green">{username}</span></p>
          )}
          {(step === 'confirm' || step === 'recovery') && (
            <p className="text-matrix-green/50">{'>'} ENCRYPTION KEY: <span className="text-matrix-green">{'•'.repeat(Math.min(password.length, 12))}</span></p>
          )}
          {step === 'recovery' && (
            <p className="text-matrix-green/50">{'>'} KEYS CONFIRMED</p>
          )}
        </div>

        {/* Active step */}
        {(step === 'username' || step === 'password' || step === 'confirm' || step === 'recovery') && (
          <div className="space-y-3">
            <label className="block font-code text-sm text-matrix-green">
              {step === 'username' && '> DESIGNATE CODENAME:'}
              {step === 'password' && '> SET ENCRYPTION KEY:'}
              {step === 'confirm'  && '> CONFIRM ENCRYPTION KEY:'}
              {step === 'recovery' && '> RECOVERY CHANNEL (optional — press Enter to skip):'}
            </label>

            <div className="flex items-center gap-2">
              <span className="font-code text-matrix-green/50 text-sm">$</span>
              <input
                ref={inputRef}
                type={isPasswordStep ? 'password' : 'text'}
                value={currentValue}
                onChange={(e) => { setCurrent(e.target.value); setFieldError(''); }}
                onKeyDown={handleKey}
                autoComplete={step === 'username' ? 'username' : step === 'recovery' ? 'email' : 'new-password'}
                className="flex-1 bg-transparent border-b border-matrix-green/50 text-matrix-green font-code text-sm pb-1 focus:outline-none focus:border-matrix-green caret-matrix-green"
                placeholder={step === 'recovery' ? 'optional@email.com (or Enter to skip)' : ''}
              />
            </div>

            {/* Username status */}
            {step === 'username' && username && (
              <p className="font-code text-[11px]">
                {usernameStatus === 'checking' && <span className="text-matrix-green/40">Checking availability...</span>}
                {usernameStatus === 'available' && <span className="text-matrix-green">✓ Codename available</span>}
                {usernameStatus === 'taken'     && <span className="text-alert-red">✗ Codename taken</span>}
              </p>
            )}

            {/* Password strength */}
            {step === 'password' && password && (
              <div className="font-code text-[11px] text-matrix-green/50 space-y-1">
                <div className="flex gap-1">
                  {PASSWORD_RULES.map((_, i) => (
                    <div
                      key={i}
                      className={`h-1 flex-1 ${i < passwordScore ? 'bg-matrix-green' : 'bg-matrix-green/15'}`}
                    />
                  ))}
                </div>
                <div className="flex flex-wrap gap-x-3">
                  {PASSWORD_RULES.map(r => (
                    <span key={r.label} className={r.test(password) ? 'text-matrix-green' : 'text-matrix-green/30'}>
                      {r.test(password) ? '✓' : '•'} {r.label}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Error */}
            {(fieldError || authError) && (
              <p className="font-code text-[11px] text-alert-red">
                {'>'} {(fieldError || authError)?.toUpperCase()}
              </p>
            )}

            {/* Action row */}
            <div className="flex items-center gap-4 pt-1">
              <button
                onClick={advance}
                className="font-code text-xs text-matrix-green border border-matrix-green/40 px-4 py-1 hover:bg-matrix-green/10 transition-colors"
              >
                {step === 'recovery' ? '> INITIALIZE' : '> CONTINUE'}
              </button>
              <span className="font-code text-[10px] text-matrix-green/20">or press Enter</span>
              <button
                onClick={onClose}
                className="ml-auto font-code text-[10px] text-matrix-green/20 hover:text-alert-red transition-colors"
              >
                ABORT
              </button>
            </div>
          </div>
        )}

        {/* Submitting / init sequence */}
        {(step === 'submitting' || step === 'done') && (
          <div className="font-code text-sm space-y-2 text-matrix-green">
            {initLines.map((line, i) => (
              <p
                key={i}
                className={i === initLines.length - 1 ? 'text-matrix-green glow-green' : 'text-matrix-green/70'}
              >
                {line}
              </p>
            ))}
            {step === 'submitting' && initLines.length < 4 && (
              <span className="animate-pulse text-matrix-green/40">_</span>
            )}
          </div>
        )}
      </div>

      {zapping && <ZapOverlay onComplete={handleZapComplete} />}
    </div>
  );
}
